import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileImport, faFilter, faReceipt, faArrowLeft, faCircleCheck } from '@fortawesome/pro-light-svg-icons';
import { Button } from '@shared/components/ui/button';
import { ConfirmModal, type ConfirmModalRef } from '@shared/components/ui/ConfirmModal';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@shared/components/ui/dropdown-menu';
import { Input } from '@shared/components/ui/input';
import { Badge } from '@shared/components/ui/badge';
import Tooltip from '@shared/components/ui/tooltip/tooltip';
import { useImportOrder } from '../../hooks';
import { ImportOrderLineItem } from './ImportOrderLineItem';

export const ImportOrderButton = ({ shopId, channelId }: { shopId: number; channelId: number }) => {
  const { t } = useTranslation('shopsModule');

  // ========================================
  // HOOK UNIFICADO PARA IMPORTACIÓN DE ÓRDENES
  // ========================================

  const {
    searchQuery,
    searchResults,
    selectedOrder,
    emptyStateImage,
    handleSearchChange,
    handleOrderSelect,
    handleBackToSearch,
    loadExternalOrdersOnce,
    isLoading,
    isImporting,
    noOrdersFound,
    handleImportOrder,
  } = useImportOrder({ shopId, channelId });

  const confirmImportRef = useRef<ConfirmModalRef>(null);

  const handleRequestImport = useCallback(() => {
    if (!selectedOrder) return;
    confirmImportRef.current?.open(
      () => handleImportOrder(),
      undefined,
      t('modals.importOrder.confirmImport.title'),
      t('modals.importOrder.confirmImport.message', { ref: selectedOrder.id }),
      t('modals.importOrder.confirmImport.confirm'),
      t('modals.importOrder.cancelButton'),
    );
  }, [handleImportOrder, selectedOrder, t]);

  const emptyListMessage = isLoading
    ? t('modals.importOrder.loading')
    : noOrdersFound
      ? t('modals.importOrder.noOrdersToImport')
      : searchQuery.trim() && searchResults.length === 0
        ? t('modals.importOrder.noSearchResults')
        : '';

  return (
    <>
      <Tooltip
        content={channelId === 1 ? t('buttons.importOrderShopify') : t('buttons.importOrder')}
        trigger={
          <DropdownMenu
            onOpenChange={(isOpen) => {
              if (isOpen) {
                loadExternalOrdersOnce({ force: true });
              }
            }}
          >
            <DropdownMenuTrigger asChild>
              {/* Import Order Button - Botón de importación de órdenes */}
              <Button variant="outline" size="sm" data-testid="import-order-button">
                <FontAwesomeIcon icon={faFileImport} className="!w-3.5 !h-3.5 text-icons" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[min(100vw-2rem,28rem)] p-0 border-card-border">
              <div className="p-4 min-h-[300px] max-h-[min(70vh,420px)] flex flex-col">
                {!selectedOrder ? (
                  // ========================================
                  // PANTALLA 1: BÚSQUEDA DE ÓRDENES
                  // ========================================
                  <>
                    {/* Header Section - Sección de encabezado */}
                    <div className="space-y-2 shrink-0">
                      <h3 className="text-base font-semibold text-foreground">{t('modals.importOrder.title')}</h3>
                      <div className="relative">
                        <FontAwesomeIcon
                          icon={faFilter}
                          className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
                          aria-hidden
                        />
                        <Input
                          placeholder={t('modals.importOrder.filterPlaceholder')}
                          className="pl-10"
                          value={searchQuery}
                          onChange={handleSearchChange}
                          data-testid="order-search-input"
                          disabled={isLoading || noOrdersFound}
                          aria-busy={isLoading}
                          autoComplete="off"
                          aria-label={t('modals.importOrder.filterPlaceholder')}
                        />
                      </div>
                    </div>

                    {/* Search Results Section - Sección de resultados de búsqueda */}
                    <div className="flex-1 mt-2 overflow-y-auto">
                      {searchResults.length > 0 ? (
                        <div className="space-y-1 border border-card-border rounded-md">
                          {searchResults.map((result) => (
                            <div key={result.id} className="border-b border-card-border !mt-0">
                              <div
                                className={`flex justify-between items-center gap-2 p-2 pl-4 rounded-md text-sm ${
                                  result.imported
                                    ? 'cursor-not-allowed opacity-65 bg-card-bg/80'
                                    : 'cursor-pointer hover:bg-hover-press'
                                }`}
                                data-testid={`order-result-item-${result.id}`}
                                data-imported={result.imported ? 'true' : undefined}
                                aria-disabled={result.imported}
                                title={result.imported ? t('modals.importOrder.alreadyImported') : undefined}
                                onClick={() => {
                                  if (!result.imported) handleOrderSelect(result.id);
                                }}
                                role={result.imported ? undefined : 'button'}
                                tabIndex={result.imported ? -1 : 0}
                                onKeyDown={(e) => {
                                  if (result.imported) return;
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    handleOrderSelect(result.id);
                                  }
                                }}
                              >
                                <span className={`truncate font-medium ${result.imported ? 'text-subtext' : ''}`}>
                                  {result.id}
                                </span>
                                <div className="flex shrink-0 items-center gap-2">
                                  {result.imported ? (
                                    <span className="flex items-center gap-1 text-xs text-success">
                                      <FontAwesomeIcon icon={faCircleCheck} className="!h-3.5 !w-3.5" aria-hidden />
                                      <span className="hidden sm:inline">
                                        {t('modals.importOrder.alreadyImported')}
                                      </span>
                                    </span>
                                  ) : null}
                                  <Badge
                                    variant="secondary"
                                    className="text-xs"
                                    title={t('modals.importOrder.linesCount', {
                                      count: result.count,
                                    })}
                                  >
                                    {t('modals.importOrder.linesCount', {
                                      count: result.count,
                                    })}
                                  </Badge>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center text-center h-full">
                          <img src={emptyStateImage} alt="" className="w-24 h-24 mb-4 opacity-90" />
                          {emptyListMessage ? <p className="text-xs text-subtext">{emptyListMessage}</p> : null}
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  // ========================================
                  // PANTALLA 2: DETALLES DE LA ORDEN
                  // ========================================
                  <>
                    {/* Order Header Section - Sección de encabezado de la orden */}
                    <div className="space-y-2 shrink-0">
                      <div className="flex flex-wrap items-center gap-2 py-2 px-3 bg-card-bg rounded-md border border-card-border">
                        <FontAwesomeIcon icon={faReceipt} className="w-4 h-4 text-icons shrink-0" aria-hidden />
                        <span className="text-sm font-medium text-foreground truncate flex-1 min-w-0">
                          {selectedOrder.id}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="shrink-0 h-8 px-2 text-xs text-primary"
                          onClick={handleBackToSearch}
                          data-testid="change-order-button"
                        >
                          <FontAwesomeIcon icon={faArrowLeft} className="w-3.5 h-3.5 mr-1" />
                          {t('modals.importOrder.changeOrder')}
                        </Button>
                      </div>
                    </div>

                    {/* Articles Section - Sección de artículos */}
                    <div className="mt-4 flex-1 overflow-y-auto">
                      <div className="mb-3 flex items-baseline justify-between gap-2">
                        <h4 className="text-sm font-semibold text-foreground">{t('modals.importOrder.articles')}</h4>
                        <span className="text-xs tabular-nums text-subtext">{selectedOrder.items.length}</span>
                      </div>

                      <div className="divide-y divide-border overflow-hidden rounded-md border border-card-border bg-card-bg">
                        {selectedOrder.items.map((item) => (
                          <div key={item.id} className="px-3 py-3" data-testid={`order-item-${item.id}`}>
                            <ImportOrderLineItem
                              item={item}
                              quantity={item.quantity}
                              data-testid={`product-${item.id}`}
                            />
                          </div>
                        ))}
                      </div>
                      {/* Import Action Section - Sección de acción de importación */}
                      <div className="mt-4">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            size="lg"
                            onClick={handleBackToSearch}
                            data-testid="cancel-import-button"
                          >
                            {t('modals.importOrder.cancelButton')}
                          </Button>
                          <Button
                            variant="primary"
                            size="lg"
                            data-testid="import-order-confirm-button"
                            onClick={handleRequestImport}
                            disabled={isImporting}
                          >
                            {isImporting ? t('common.loading') : t('modals.importOrder.importButton')}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        }
      />
      <ConfirmModal ref={confirmImportRef} />
    </>
  );
};
